/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {useIntl} from 'react-intl'
import {validatePassword} from '@salesforce/retail-react-app/app/utils/password-utils'
import {TermsAndConditionsLabel} from './terms-and-conditions-label'
import {formatPhoneNumber} from '../../utils/phone-utils'
import {useBrand} from '../../contexts/brand-context'

export default function useRegistrationFields({
    form: {
        control,
        formState: {errors}
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()
    const {isGarage} = useBrand()

    const fields = {
        firstName: {
            name: `${prefix}firstName`,
            label: formatMessage({
                defaultMessage: 'First name',
                id: 'use_registration_fields.label.first_name'
            }),
            type: 'text',
            autoComplete: 'given-name',
            defaultValue: '',
            rules: {
                validate: (value) =>
                    !value || value?.trim().length || isGarage
                        ? formatMessage({
                              defaultMessage: 'Please enter your first name',
                              id: 'use_registration_fields.error.required_first_name.garage'
                          })
                        : formatMessage({
                              defaultMessage: 'Please enter your first name',
                              id: 'use_registration_fields.error.required_first_name.dynamite'
                          })
            },
            error: errors[`${prefix}firstName`],
            control,
            variant: 'webFloatingField'
        },
        lastName: {
            name: `${prefix}lastName`,
            label: formatMessage({
                defaultMessage: 'Last name',
                id: 'use_registration_fields.label.last_name'
            }),
            type: 'text',
            defaultValue: '',
            autoComplete: 'family-name',
            rules: {
                validate: (value) =>
                    !value || value?.trim().length || isGarage
                        ? formatMessage({
                              defaultMessage: 'Please enter your last name.',
                              id: 'use_registration_fields.error.required_last_name.garage'
                          })
                        : formatMessage({
                              defaultMessage: 'Please enter your last name.',
                              id: 'use_registration_fields.error.required_last_name.dynamite'
                          })
            },
            error: errors[`${prefix}lastName`],
            control,
            variant: 'webFloatingField'
        },
        phone: {
            name: `${prefix}phone`,
            label: formatMessage({
                defaultMessage: 'Phone Number (optional)',
                id: 'use_registration_fields.label.phone'
            }),
            placeholder: formatMessage({
                defaultMessage: 'Phone Number (optional)',
                id: 'use_registration_fields.label.phone'
            }),
            defaultValue: '',
            type: 'tel',
            autoComplete: 'tel',
            rules: {
                validate: (value) => {
                    if (!value) return true
                    const digits = String(value ?? '').replace(/[^\d]/g, '')
                    if (digits.length !== 10) {
                        return formatMessage({
                            defaultMessage: 'Please enter a valid phone number',
                            id: 'use_address_fields.error.invalid_phone_format'
                        })
                    }
                    return true
                }
            },
            error: errors[`${prefix}phone`],
            inputProps: ({onChange}) => ({
                inputMode: 'numeric',
                onChange(evt) {
                    onChange(formatPhoneNumber(evt.target.value))
                }
            }),
            control,
            variant: 'webFloatingField'
        },
        birthDay: {
            name: `${prefix}birthDay`,
            label: formatMessage({
                defaultMessage: 'Birthday',
                id: 'use_registration_fields.label.birthday'
            }),
            type: 'date',
            defaultValue: '',
            autoComplete: 'birthDay',
            inputProps: {
                min: `${new Date().getFullYear() - 100}-01-01`,
                max: new Date().toISOString().split('T')[0],
                pr: 0,
                onKeyDown: (e) => {
                    const allowedKeys = ['Tab', 'Enter', 'Escape']
                    if (!allowedKeys.includes(e.key)) {
                        e.preventDefault()
                    }
                },
                onPaste: (e) => e.preventDefault()
            },
            rules: {
                validate: (value) => {
                    if (!value) return true

                    const birthDate = new Date(value)
                    const today = new Date()

                    const age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000))

                    if (age < 16) {
                        return isGarage
                            ? formatMessage({
                                  defaultMessage: 'You must be at least 16 years old',
                                  id: 'use_registration_fields.error.min_age.garage'
                              })
                            : formatMessage({
                                  defaultMessage: 'You must be at least 16 years old',
                                  id: 'use_registration_fields.error.min_age.dynamite'
                              })
                    }

                    return true
                }
            },
            error: errors[`${prefix}birthDay`],
            control,
            variant: 'webFloatingField'
        },
        email: {
            name: `${prefix}email`,
            label: formatMessage({
                defaultMessage: 'Email address',
                id: 'use_registration_fields.label.email'
            }),
            type: 'email',
            autoComplete: 'email',
            defaultValue: '',
            rules: {
                required: isGarage
                    ? formatMessage({
                          defaultMessage: 'Enter your email address',
                          id: 'use_registration_fields.error.empty_email.garage'
                      })
                    : formatMessage({
                          defaultMessage: 'Enter your email address',
                          id: 'use_registration_fields.error.empty_email.dynamite'
                      }),
                pattern: {
                    value: /^.+@.+\.[a-zA-Z]{2,3}$/,
                    message: formatMessage({
                        defaultMessage: 'Enter a valid email address such as name@email.com',
                        id: 'use_registration_fields.error.pattern_email'
                    })
                }
            },
            error: errors[`${prefix}email`],
            control,
            variant: 'webFloatingField'
        },
        password: {
            name: `${prefix}password`,
            label: formatMessage({
                defaultMessage: 'Password',
                id: 'use_registration_fields.label.password'
            }),
            type: 'password',
            defaultValue: '',
            rules: {
                required: isGarage
                    ? formatMessage({
                          defaultMessage: 'Enter your password',
                          id: 'use_registration_fields.error.required_password.garage'
                      })
                    : formatMessage({
                          defaultMessage: 'Enter your password',
                          id: 'use_registration_fields.error.required_password.dynamite'
                      }),
                validate: {
                    hasMinChars: (val) =>
                        validatePassword(val).hasMinChars ||
                        formatMessage({
                            defaultMessage:
                                'Password must be at least 8 characters long, include uppercase and lowercase letters, one special character, at least one number, and must not contain any part of your email address.',
                            id: 'password_requirements.error'
                        }),
                    hasUppercase: (val) =>
                        validatePassword(val).hasUppercase ||
                        formatMessage({
                            defaultMessage:
                                'Password must be at least 8 characters long, include uppercase and lowercase letters, one special character, at least one number, and must not contain any part of your email address.',
                            id: 'password_requirements.error'
                        }),
                    hasLowercase: (val) =>
                        validatePassword(val).hasLowercase ||
                        formatMessage({
                            defaultMessage:
                                'Password must be at least 8 characters long, include uppercase and lowercase letters, one special character, at least one number, and must not contain any part of your email address.',
                            id: 'password_requirements.error'
                        }),
                    hasNumber: (val) =>
                        validatePassword(val).hasNumber ||
                        formatMessage({
                            defaultMessage:
                                'Password must be at least 8 characters long, include uppercase and lowercase letters, one special character, at least one number, and must not contain any part of your email address.',
                            id: 'password_requirements.error'
                        }),
                    hasSpecialChar: (val) =>
                        validatePassword(val).hasSpecialChar ||
                        formatMessage({
                            defaultMessage:
                                'Password must be at least 8 characters long, include uppercase and lowercase letters, one special character, at least one number, and must not contain any part of your email address.',
                            id: 'password_requirements.error'
                        })
                }
            },
            inputProps: {
                maxLength: 255
            },
            error: errors[`${prefix}password`],
            hideErrorMessage: true,
            control,
            variant: 'webFloatingField'
        },
        passwordConfirm: {
            name: `${prefix}passwordConfirm`,
            label: formatMessage({
                defaultMessage: 'Confirm password',
                id: 'use_registration_fields.label.password.confirm'
            }),
            type: 'password',
            defaultValue: '',
            rules: {
                required: isGarage
                    ? formatMessage({
                          defaultMessage: 'Enter your password again',
                          id: 'use_registration_fields.error.empty_confirm_password.garage'
                      })
                    : formatMessage({
                          defaultMessage: 'Enter your password again',
                          id: 'use_registration_fields.error.empty_confirm_password.dynamite'
                      }),
                validate: {
                    isValidLength: (value) =>
                        (value.length >= 8 && value.length <= 255) ||
                        formatMessage({
                            defaultMessage: 'This field needs 8 to 255 characters.',
                            id: 'use_registration_fields.error.password_confirm_length'
                        }),
                    matchesPassword: (value, formValues) =>
                        value === formValues?.[`${prefix}password`] ||
                        formatMessage({
                            defaultMessage: 'Does not match new password',
                            id: 'use_registration_fields.error.passwords_do_not_match'
                        })
                }
            },
            error: errors[`${prefix}passwordConfirm`],
            control,
            variant: 'webFloatingField'
        },
        termsAndConditions: {
            name: `${prefix}termsAndConditions`,
            label: <TermsAndConditionsLabel />,
            type: 'checkbox',
            defaultValue: false,
            rules: {
                required: formatMessage({
                    id: 'use_registration.terms.and.conditions.error',
                    defaultMessage: 'Terms and conditions must be accepted'
                })
            },
            error: errors[`${prefix}termsAndConditions`],
            control,
            inputProps: {
                alignItems: 'flex-start',
                fontSize: 'xs',
                color: 'primaryBlack'
            }
        },
        emailSubscribe: {
            name: `${prefix}emailSubscribe`,
            label: formatMessage({
                defaultMessage:
                    'Yes, I would like to receive commercial electronic messages, including a newsletter and discount notifications. I understand I can unsubscribe at any time.',
                id: 'use_registration_fields.label.sign_up_to_emails'
            }),
            type: 'checkbox',
            defaultValue: false,
            error: errors[`${prefix}emailSubscribe`],
            control,
            inputProps: {
                alignItems: 'flex-start',
                fontSize: 'xs',
                color: 'primaryBlack'
            },
            labelProps: {
                fontSize: 'xs',
                lineHeight: '1.5'
            }
        }
    }

    return fields
}
